import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Shield,
  Hammer,
  Wrench,
  CloudLightning,
  MapPin,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { CityServicesSection } from "@/components/CrossLinks";

export default function Saskatoon() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Service Areas",
          item: "https://www.saskroofingcompany.com/areas",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Saskatoon",
          item: "https://www.saskroofingcompany.com/areas/saskatoon",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "RoofingContractor",
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
      name: "Sask Roofing Company",
      url: "https://www.saskroofingcompany.com/",
      telephone: "+1-639-739-7969",
      email: "info@saskroofingcompany.com",
      areaServed: {
        "@type": "City",
        name: "Saskatoon",
        containedInPlace: { "@type": "State", name: "Saskatchewan" },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Saskatoon?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Generally, a standard residential roof replacement (shingles over existing decking) does not require a building permit in Saskatoon. However, if structural repairs to the trusses or decking are needed, a permit may be required. We handle all necessary assessments and compliance.",
          },
        },
        {
          "@type": "Question",
          name: "How quickly can you respond to a roof leak in Saskatoon?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We prioritize active leaks and storm damage. In most cases, we can have an estimator out for a roof repair in Saskatoon within 24-48 hours to secure your property and prevent further interior damage.",
          },
        },
        {
          "@type": "Question",
          name: "Do you work with local insurance adjusters for hail damage?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, we frequently work with homeowners and their insurance companies following major Saskatoon hailstorms. We provide detailed inspection reports and photos to help ensure your claim is processed smoothly.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Roofing Company Saskatoon | Sask Roofing Company"
        description="Looking for a reliable roofing company in Saskatoon? We provide expert roof repair, replacement, and storm damage restoration for Saskatoon homes."
        schema={schema}
      />

      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/78aa047c-2ebb-4e6e-a89c-b9e0f92fe325.png&output=webp&q=60"
            alt="Saskatoon Roofing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Roofing Company Saskatoon
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              When you need a reliable roofing company near you in the Bridge
              City, we deliver honest pricing, premium materials, and expert
              installation built specifically for Saskatoon's extreme
              temperature swings and summer storms.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
                asChild
              >
                <Link to="/contact">Get a Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white hover:text-primary shadow-lg"
                asChild
              >
                <a href="tel:6397397969">Call Now (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stat Callout Grid */}
      <section className="py-12 bg-background border-b">
        <div className="container px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-4xl font-bold text-primary mb-2">
                1,500+
              </span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Roofs Completed
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-4xl font-bold text-primary mb-2">
                24-48h
              </span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Avg. Response Time
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-4xl font-bold text-primary mb-2">15+</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Years Serving Saskatoon
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* City Context Card & Built for Climate */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent font-medium text-sm">
                <MapPin className="w-4 h-4" />
                Serving Saskatoon & Surrounding Areas
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">
                Built for Saskatoon's Climate
              </h2>

              {/* City Context Card */}
              <div className="p-6 bg-muted/50 border-l-4 border-primary rounded-r-xl">
                <p className="font-medium text-foreground">
                  Saskatoon experiences extreme temperature swings, dropping to
                  -40°C in the winter and soaring above +35°C in the summer.
                  Coupled with intense summer hailstorms, your roof requires
                  specialized materials and ventilation to survive.
                </p>
              </div>

              <p className="text-lg text-muted-foreground">
                As an experienced roofing contractor in Saskatoon, we don't just
                nail down generic shingles. We install complete roofing systems
                engineered specifically for the prairies. This means utilizing
                Class 3 and Class 4 impact-resistant shingles for hail defense,
                installing high-wind nailing patterns to combat open-prairie
                gusts, and ensuring proper attic ventilation to prevent winter
                ice damming along your eaves.
              </p>
              <ul className="space-y-4 pt-4">
                {[
                  "Impact-resistant shingles for summer hail protection",
                  "Proper ventilation to prevent winter ice damming",
                  "High-wind nailing patterns for open-prairie gusts",
                  "Premium ice and water shield installed past the warm wall line",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[500px]">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/c8a30077-d2e9-4720-8ad2-8b08f84617d6.png&output=webp&q=60"
                alt="Roofing crew working in Saskatoon"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Our Saskatoon Roofing Services
            </h2>
            <p className="text-lg text-muted-foreground">
              Whether you need a minor leak patched before the next storm or a
              complete roof replacement, our local crews deliver premium
              craftsmanship backed by real warranties.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/areas/saskatoon/replacement"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <Shield className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Roof Replacement</h3>
              <p className="text-muted-foreground text-sm">
                Complete tear-offs and expert installations of new,
                climate-tested roofing systems in Saskatoon.
              </p>
            </Link>

            <Link
              to="/areas/saskatoon/repair"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <Wrench className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Roof Repair</h3>
              <p className="text-muted-foreground text-sm">
                Fast, reliable fixes for missing shingles, leaks, and wind
                damage across Saskatoon.
              </p>
            </Link>

            <Link
              to="/services/storm-damage"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <CloudLightning className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Storm Restoration</h3>
              <p className="text-muted-foreground text-sm">
                Comprehensive emergency tarping and restoration after severe
                Saskatoon hailstorms.
              </p>
            </Link>

            <Link
              to="/services/inspections"
              className="group bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-all"
            >
              <Hammer className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold mb-2">Roof Inspections</h3>
              <p className="text-muted-foreground text-sm">
                Thorough, honest assessments for peace of mind or real estate
                transactions.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Cost Context & Comparison Table */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-5xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Honest Roofing Costs in Saskatoon
            </h2>
            <p className="text-lg text-muted-foreground">
              We believe in transparent, fixed-price quotes. While costs vary
              based on the size, pitch, and complexity of your roof, here is a
              general breakdown of what Saskatoon homeowners can expect. We
              provide detailed written estimates with no hidden fees.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="p-4 font-bold border-b">Service Type</th>
                  <th className="p-4 font-bold border-b">Estimated Cost</th>
                  <th className="p-4 font-bold border-b">Best For</th>
                  <th className="p-4 font-bold border-b">Typical Timeline</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="p-4 font-medium">
                    Asphalt Shingle Replacement
                  </td>
                  <td className="p-4 text-muted-foreground">
                    $6,000 - $12,000
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Most residential homes, budget-friendly durability
                  </td>
                  <td className="p-4 text-muted-foreground">1-2 Days</td>
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="p-4 font-medium">Metal Roof Installation</td>
                  <td className="p-4 text-muted-foreground">
                    $12,000 - $25,000+
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Maximum lifespan, ultimate hail/snow resistance
                  </td>
                  <td className="p-4 text-muted-foreground">3-5 Days</td>
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="p-4 font-medium">Targeted Roof Repair</td>
                  <td className="p-4 text-muted-foreground">$400 - $1,500</td>
                  <td className="p-4 text-muted-foreground">
                    Fixing isolated leaks, blown-off shingles, or flashing
                  </td>
                  <td className="p-4 text-muted-foreground">2-4 Hours</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground text-center mt-4">
            * These are rough estimates based on average Saskatoon home sizes.
            Contact us for a precise, fixed-price quote.
          </p>
        </div>
      </section>

      {/* All Services Grid */}
      <CityServicesSection city="Saskatoon" citySlug="saskatoon" />

      {/* FAQ */}
      <section className="py-20 bg-muted/30 border-t">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Saskatoon Roofing FAQ
          </h2>
          <Accordion
            type="single"
            collapsible
            className="w-full bg-card rounded-xl border p-4 shadow-sm"
          >
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                Do I need a building permit for a roof replacement in Saskatoon?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Generally, a standard residential roof replacement (shingles
                over existing decking) does not require a building permit in
                Saskatoon. However, if structural repairs to the trusses or
                decking are needed, a permit may be required. We handle all
                necessary assessments and compliance.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                How quickly can you respond to a roof leak in Saskatoon?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                We prioritize active leaks and storm damage. In most cases, we
                can have an estimator out for a roof repair in Saskatoon within
                24-48 hours to secure your property and prevent further interior
                damage.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Do you work with local insurance adjusters for hail damage?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Yes, we frequently work with homeowners and their insurance
                companies following major Saskatoon hailstorms. We provide
                detailed inspection reports and photos to help ensure your claim
                is processed smoothly.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Protect Your Saskatoon Home?
          </h2>
          <p className="text-xl text-primary-foreground/80">
            No pressure, no obligation. Get an honest, expert assessment of your
            roof today.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Schedule a Free Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary"
              asChild
            >
              <a href="tel:6397397969">Call Now (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
